// FILE: frontend/src/components/kyb/sections/BusinessProfileSection.tsx
// COMPLETE FIXED VERSION with named export

import React from 'react';
import { Globe, MapPin, Users, Calendar, Building } from 'lucide-react';
import type { BusinessProfile, BusinessProfileQuality } from '../../../types/kyb.types';

interface BusinessProfileSectionProps {
  businessProfile: BusinessProfile | null;
  businessProfileQuality: BusinessProfileQuality | null;
}

export const BusinessProfileSection: React.FC<BusinessProfileSectionProps> = ({
  businessProfile,
  businessProfileQuality
}) => {
  if (!businessProfile) return null;

  const completeness = businessProfileQuality?.completeness_score || 0;

  return (
    <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-700 to-slate-900 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Globe className="w-6 h-6 text-white" />
            <h3 className="text-xl font-bold text-white">Web-Sourced Business Information</h3>
          </div>
          <span className="px-3 py-1 bg-slate-600 text-white text-sm font-bold rounded-full">
            {Math.round(completeness * 100)}% Complete
          </span>
        </div>
      </div>

      {/* Content Grid */}
      <div className="p-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {businessProfile.primary_domain && (
            <div className="p-4 bg-slate-50 rounded-lg">
              <p className="text-xs font-semibold text-slate-600 uppercase mb-1">Primary Domain</p>
              <p className="text-base font-bold text-slate-900 break-all">{businessProfile.primary_domain}</p>
              {businessProfile.headquarters_country && (
                <span className="text-xs text-slate-500 mt-1 block">🌍 {businessProfile.headquarters_country}</span>
              )}
            </div>
          )}
          
          {businessProfile.employee_count_range && (
            <div className="p-4 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-2 mb-1">
                <Users className="w-4 h-4 text-slate-600" />
                <p className="text-xs font-semibold text-slate-600 uppercase">Company Size</p>
              </div>
              <p className="text-base font-bold text-slate-900">{businessProfile.employee_count_range}</p>
            </div>
          )}

          {businessProfile.industry && (
            <div className="p-4 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-2 mb-1">
                <Building className="w-4 h-4 text-slate-600" />
                <p className="text-xs font-semibold text-slate-600 uppercase">Industry</p>
              </div>
              <p className="text-base font-bold text-slate-900">{businessProfile.industry}</p>
            </div>
          )}

          {businessProfile.founded_year && (
            <div className="p-4 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-2 mb-1">
                <Calendar className="w-4 h-4 text-slate-600" />
                <p className="text-xs font-semibold text-slate-600 uppercase">Founded</p>
              </div>
              <p className="text-base font-bold text-slate-900">{businessProfile.founded_year}</p>
            </div>
          )}

          {businessProfile.business_type && (
            <div className="p-4 bg-slate-50 rounded-lg">
              <p className="text-xs font-semibold text-slate-600 uppercase mb-1">Business Type</p>
              <p className="text-base font-bold text-slate-900">{businessProfile.business_type}</p>
            </div>
          )}

          {businessProfile.headquarters_country && !businessProfile.primary_domain && (
            <div className="p-4 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-2 mb-1">
                <MapPin className="w-4 h-4 text-slate-600" />
                <p className="text-xs font-semibold text-slate-600 uppercase">Headquarters</p>
              </div>
              <p className="text-base font-bold text-slate-900">{businessProfile.headquarters_country}</p>
            </div>
          )}
        </div>

        {businessProfile.company_registrations && businessProfile.company_registrations.length > 0 && (
          <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-xs font-semibold text-slate-700 uppercase mb-2">Associated Registrations:</p>
            <div className="flex flex-wrap gap-2">
              {businessProfile.company_registrations.map((reg, idx) => (
                <span key={idx} className="px-3 py-1 bg-blue-200 text-blue-800 text-sm font-medium rounded-full">
                  {reg}
                </span>
              ))}
            </div>
          </div>
        )}

        {businessProfile.ownership_status && (
          <div className="mt-4 p-4 bg-purple-50 rounded-lg border border-purple-200">
            <p className="text-xs font-semibold text-purple-700 uppercase mb-1">Ownership Status</p>
            <p className="text-sm text-purple-900 font-medium">{businessProfile.ownership_status}</p>
          </div>
        )}
      </div>
    </div>
  );
};